import React, { useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  CircularProgress,
  Typography,
  InputAdornment,
  Button,
  Checkbox,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { revisarNotificacionesPI, resolverNotificacionesPI, consultarDetalleLeadPI } from "../../action";

const RevisionNotificacionesPI = () => {
  const dispatch = useDispatch();

  // Filters State
  const [fechaInicio, setFechaInicio] = useState(
    moment().subtract(2, "days").format("YYYY-MM-DD")
  );
  const [fechaFin, setFechaFin] = useState(moment().format("YYYY-MM-DD"));
  const [topic, setTopic] = useState("vis_leads");

  // Loaders and Results State
  const [loadingScan, setLoadingScan] = useState(false);
  const [loadingResolve, setLoadingResolve] = useState(false);
  const [results, setResults] = useState(null);

  // Table & Live Search State
  const [filterSearch, setFilterSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Selection & Resolution Action State
  const [selectedResources, setSelectedResources] = useState([]);
  const [accionResolucion, setAccionResolucion] = useState("silencioso");

  // Lead Detail Dialog State
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleOpenDetail = async (resource) => {
    setLoadingDetail(true);
    setSelectedLeadDetail(null);
    setOpenDetailDialog(true);
    try {
      const data = await dispatch(consultarDetalleLeadPI(resource));
      if (data) {
        setSelectedLeadDetail(data);
      } else {
        setOpenDetailDialog(false);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo obtener el detalle de la notificación.", "error");
      setOpenDetailDialog(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setOpenDetailDialog(false);
    setSelectedLeadDetail(null);
  };

  // Submit scan
  const handleScan = async (e) => {
    e.preventDefault();
    if (!fechaInicio || !fechaFin) {
      Swal.fire("Atención", "Debe seleccionar un rango de fechas válido.", "warning");
      return;
    }

    if (moment(fechaInicio).isAfter(fechaFin)) {
      Swal.fire("Atención", "La fecha de inicio no puede ser posterior a la fecha de fin.", "warning");
      return;
    }

    setLoadingScan(true);
    setResults(null);
    setSelectedResources([]);
    setPage(0);

    try {
      const payload = {
        fechaInicio: moment(fechaInicio).startOf("day").toISOString(),
        fechaFin: moment(fechaFin).endOf("day").toISOString(),
        topic,
        accionNoRegistradas: "solo_revisar" // Scan only mode
      };

      const res = await dispatch(revisarNotificacionesPI(payload));
      if (res) {
        setResults(res);
        const countFaltantes = res.detalles.filter((d) => d.estado === "Faltante").length;
        Swal.fire({
          title: "Escaneo completado",
          text: `Se encontraron ${countFaltantes} notificaciones sin su correspondiente lead registrado.`,
          icon: countFaltantes > 0 ? "info" : "success",
          confirmButtonColor: "#00a62b"
        });
      } else {
        Swal.fire("Error", "No se recibieron resultados del escaneo.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Ocurrió un error inesperado al procesar el escaneo.", "error");
    } finally {
      setLoadingScan(false);
    }
  };

  // Submit resolution for selected resources
  const handleResolve = async () => {
    if (selectedResources.length === 0) {
      Swal.fire("Atención", "Debe seleccionar al menos una notificación faltante para resolver.", "warning");
      return;
    }

    const actionText =
      accionResolucion === "correo"
        ? "Se registrarán los leads y se enviarán correos de notificación habituales a los brokers correspondientes."
        : "Se registrarán los leads de forma silenciosa en la base de datos sin enviar correos electrónicos.";

    const confirm = await Swal.fire({
      title: `¿Confirmar resolución de ${selectedResources.length} leads?`,
      text: actionText,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#00a62b",
      cancelButtonColor: "#d33"
    });

    if (!confirm.isConfirmed) return;

    setLoadingResolve(true);

    try {
      const payload = {
        resources: selectedResources,
        topic,
        accion: accionResolucion
      };

      const res = await dispatch(resolverNotificacionesPI(payload));
      if (res) {
        // Build success message detailing errors or successes
        const errores = res.detalles.filter((d) => d.estado === "Error");
        const exitos = res.detalles.filter((d) => d.estado !== "Error").length;

        if (errores.length > 0) {
          Swal.fire({
            title: "Resolución parcial",
            text: `Se registraron con éxito ${exitos} leads. Ocurrieron errores en ${errores.length} recursos.`,
            icon: "warning",
            confirmButtonColor: "#00a62b"
          });
        } else {
          Swal.fire({
            title: "¡Éxito!",
            text: `Se registraron con éxito los ${exitos} leads seleccionados.`,
            icon: "success",
            confirmButtonColor: "#00a62b"
          });
        }

        // Update the results state locally to reflect the resolution in real time
        const updatedDetalles = results.detalles.map((original) => {
          const processed = res.detalles.find((p) => p.resource === original.resource);
          if (processed) {
            return {
              ...original,
              estado: processed.estado,
              detalleError: processed.detalleError
            };
          }
          return original;
        });

        // Recompute KPIs based on updated details
        const yaRegistradas = updatedDetalles.filter((d) => d.estado === "Ya registrado").length;
        const procesadas = updatedDetalles.filter(
          (d) => d.estado === "Registrado silenciosamente" || d.estado === "Registrado y notificado"
        ).length;

        setResults({
          ...results,
          yaRegistradas,
          procesadas,
          detalles: updatedDetalles
        });

        setSelectedResources([]); // Clear selections
      } else {
        Swal.fire("Error", "No se recibió respuesta de la resolución.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Ocurrió un error inesperado al resolver.", "error");
    } finally {
      setLoadingResolve(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Checkbox selection helpers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Select only rows that are "Faltante" in the filtered dataset
      const newSelecteds = filteredDetails
        .filter((n) => n.estado === "Faltante")
        .map((n) => n.resource);
      setSelectedResources(newSelecteds);
    } else {
      setSelectedResources([]);
    }
  };

  const handleCheckboxClick = (event, resource) => {
    const selectedIndex = selectedResources.indexOf(resource);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedResources, resource);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedResources.slice(1));
    } else if (selectedIndex === selectedResources.length - 1) {
      newSelected = newSelected.concat(selectedResources.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedResources.slice(0, selectedIndex),
        selectedResources.slice(selectedIndex + 1)
      );
    }

    setSelectedResources(newSelected);
  };

  const isSelected = (resource) => selectedResources.indexOf(resource) !== -1;

  // Filtered details list for live search
  const filteredDetails = results?.detalles
    ? results.detalles.filter((item) =>
      item.resource.toLowerCase().includes(filterSearch.toLowerCase())
    )
    : [];

  const faltantesCount = results?.detalles
    ? results.detalles.filter((d) => d.estado === "Faltante").length
    : 0;

  const getChipProps = (estado) => {
    switch (estado) {
      case "Ya registrado":
        return { label: "Ya registrado", color: "default", variant: "outlined" };
      case "Faltante":
        return { label: "Faltante en base de datos", color: "warning", variant: "filled" };
      case "Registrado silenciosamente":
        return { label: "Registrado silencioso", color: "success", variant: "filled" };
      case "Registrado y notificado":
        return { label: "Registrado y notificado", color: "primary", variant: "filled" };
      case "Error":
        return { label: "Error al resolver", color: "error", variant: "filled" };
      default:
        return { label: estado, color: "warning", variant: "outlined" };
    }
  };

  return (
    <div className="contenido">
      <div className="header">
        <h2>Revisión de Notificaciones Portalinmobiliario</h2>
      </div>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Scan Filters Form */}
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(224, 224, 224, 0.5)"
            }}
          >
            <form onSubmit={handleScan}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Fecha de Inicio"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    fullWidth
                    required
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Fecha de Fin"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    fullWidth
                    required
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Tópico"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    fullWidth
                    required
                    sx={{ bgcolor: "white" }}
                  >
                    <MenuItem value="vis_leads">vis_leads</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loadingScan || loadingResolve}
                    startIcon={loadingScan ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{
                      backgroundColor: "#00a62b",
                      "&:hover": {
                        backgroundColor: "#008522"
                      },
                      textTransform: "none",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      px: 4,
                      py: 1
                    }}
                  >
                    {loadingScan ? "Escaneando..." : "Escanear y Buscar Diferencias"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Results section */}
        {results && (
          <>
            {/* KPI Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, textAlign: 'center', borderColor: '#e0e0e0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333', lineHeight: 1.2 }}>
                    Notificaciones Revisadas
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {results.totalRevisadas}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, textAlign: 'center', borderColor: '#e0e0e0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333', lineHeight: 1.2 }}>
                    Ya Registradas en BD
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00a62b' }}>
                    {results.yaRegistradas}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, textAlign: 'center', borderColor: '#e0e0e0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333', lineHeight: 1.2 }}>
                    Diferencias / Faltantes
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: faltantesCount > 0 ? '#d97706' : '#00a62b' }}>
                    {faltantesCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, textAlign: 'center', borderColor: '#e0e0e0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333', lineHeight: 1.2 }}>
                    Resueltas en esta Sesión
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00a62b' }}>
                    {results.procesadas}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Resolution Control Bar (Displays when differences are selected) */}
            {selectedResources.length > 0 && (
              <Grid item xs={12}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    borderLeft: "6px solid #00a62b",
                    backgroundColor: "#f9fafb",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 3
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                      Resolución de Diferencias
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Has seleccionado <strong>{selectedResources.length}</strong> recursos faltantes. Elige el método de registro para procesar.
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={accionResolucion}
                        onChange={(e) => setAccionResolucion(e.target.value)}
                      >
                        <FormControlLabel
                          value="silencioso"
                          control={<Radio size="small" color="success" />}
                          label={<span style={{ fontSize: "0.9rem" }}>Registrar Silenciosamente</span>}
                        />
                        <FormControlLabel
                          value="correo"
                          control={<Radio size="small" color="success" />}
                          label={<span style={{ fontSize: "0.9rem" }}>Registrar y Enviar Correo</span>}
                        />
                      </RadioGroup>
                    </FormControl>

                    <Button
                      variant="contained"
                      onClick={handleResolve}
                      disabled={loadingResolve}
                      startIcon={loadingResolve ? <CircularProgress size={20} color="inherit" /> : <SettingsBackupRestoreIcon />}
                      sx={{
                        backgroundColor: "#00a62b",
                        "&:hover": {
                          backgroundColor: "#008522"
                        },
                        textTransform: "none",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        px: 3,
                        py: 1
                      }}
                    >
                      {loadingResolve ? "Resolviendo..." : "Resolver Seleccionadas"}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Detailed Table */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: "12px" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Detalle de Conciliación Local
                  </Typography>
                  <TextField
                    placeholder="Buscar por recurso..."
                    size="small"
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon size="small" />
                        </InputAdornment>
                      )
                    }}
                    sx={{ width: { xs: "100%", sm: 280 } }}
                  />
                </Box>

                {filteredDetails.length > 0 ? (
                  <TableContainer>
                    <Table size="medium">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ backgroundColor: "#f8f9fa" }}>
                            <Checkbox
                              indeterminate={
                                selectedResources.length > 0 &&
                                selectedResources.length <
                                filteredDetails.filter((n) => n.estado === "Faltante").length
                              }
                              checked={
                                filteredDetails.filter((n) => n.estado === "Faltante").length > 0 &&
                                selectedResources.length ===
                                filteredDetails.filter((n) => n.estado === "Faltante").length
                              }
                              onChange={handleSelectAllClick}
                              inputProps={{ "aria-label": "Select all missing notifications" }}
                              disabled={filteredDetails.filter((n) => n.estado === "Faltante").length === 0}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Recurso</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Tópico</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Fecha Notificación (ML)</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Fecha Registro (Mongo)</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Estado Proceso</TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Detalle / Error</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDetails
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, index) => {
                            const isItemSelected = isSelected(row.resource);
                            const isSelectable = row.estado === "Faltante";
                            const chipProps = getChipProps(row.estado);
                            return (
                              <TableRow
                                key={index}
                                hover
                                role="checkbox"
                                aria-checked={isItemSelected}
                                selected={isItemSelected}
                                sx={{ cursor: isSelectable ? "pointer" : "default" }}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isItemSelected}
                                    disabled={!isSelectable}
                                    onChange={(event) => handleCheckboxClick(event, row.resource)}
                                    inputProps={{ "aria-labelledby": `table-checkbox-${index}` }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                                  {row.resource}
                                </TableCell>
                                <TableCell align="center">{row.topic}</TableCell>
                                <TableCell align="center">
                                  {row.sent ? moment(row.sent).format("DD/MM/YYYY HH:mm:ss") : "-"}
                                </TableCell>
                                <TableCell align="center">
                                  {row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY HH:mm:ss") : "-"}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip size="small" {...chipProps} />
                                </TableCell>
                                <TableCell sx={{ color: row.estado === "Error" ? "error.main" : "text.secondary" }}>
                                  {row.detalleError || "-"}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Ver detalle del lead en Mercado Libre">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenDetail(row.resource);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={filteredDetails.length}
                      page={page}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      labelRowsPerPage="Registros por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 6,
                      color: "text.secondary"
                    }}
                  >
                    <ErrorOutlineIcon sx={{ fontSize: 48, mb: 1, color: "rgba(0, 0, 0, 0.15)" }} />
                    <Typography>No se encontraron registros de conciliación que coincidan con el filtro.</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </>
        )}
      </Grid>

      {/* Dialog Modal for Lead Details */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Detalle del Lead (Mercado Libre)</span>
          {loadingDetail && <CircularProgress size={24} />}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 2 }}>
              <CircularProgress color="success" />
              <Typography variant="body2" color="text.secondary">
                Consultando API de Mercado Libre...
              </Typography>
            </Box>
          ) : selectedLeadDetail ? (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Recurso / URI
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace", bgcolor: "#f5f5f5", p: 1, borderRadius: 1 }}>
                  {selectedLeadDetail.id || selectedLeadDetail.item_id ? `/vis/leads/${selectedLeadDetail.id}` : "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Nombre del Cliente
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedLeadDetail.name || "No especificado"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tipo de Contacto
                </Typography>
                <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                  {selectedLeadDetail.contact_type || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body1">
                  {selectedLeadDetail.phone || "No registrado"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                  {selectedLeadDetail.email || "No registrado"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  ID de Publicación (PI)
                </Typography>
                <Typography variant="body1">
                  {selectedLeadDetail.item_id || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Propiedad Propins
                </Typography>
                <Typography variant="body1" color={selectedLeadDetail.CodigoPropiedad ? "success.main" : "text.secondary"} fontWeight="bold">
                  {selectedLeadDetail.CodigoPropiedad || "Sin coincidencia en base de datos"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Fecha Creación en ML
                </Typography>
                <Typography variant="body1">
                  {selectedLeadDetail.created_at ? moment(selectedLeadDetail.created_at).format("DD/MM/YYYY HH:mm:ss") : "-"}
                </Typography>
              </Grid>

            </Grid>
          ) : (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography color="error">No se encontraron detalles para este lead.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail} sx={{ textTransform: "none", fontWeight: "bold" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RevisionNotificacionesPI;
